import React, { useCallback, useMemo } from 'react';
import { useCurrentDecedent } from '@/app/providers/DecedentProvider';
import FormConstructor from '@/common/containers/FormConstructor/index';
import Loader from '@/common/components/Loader/index';
import { useTranslation } from 'react-i18next';
import { useQuestionListQuery } from '@/entities/question';

const DISABLED_BY_CHECKBOX = [
  'addressLine1',
  'addressLine2',
  'country',
  'city',
  'province',
  'postalCode',
];

const DecedentDependants = ({ onSubmit, ...props }) => {
  const { t } = useTranslation();
  const decedent = useCurrentDecedent();
  const questionListQuery = useQuestionListQuery('basic_dependants', decedent?.id);
  const payload = useMemo(() => {
    if (!questionListQuery.data) {
      return {};
    }

    const { list, answers } = questionListQuery.data;
    const formatted = {
      questions: list[0].questions,
      answers,
    };

    if (formatted.questions.length) {
      formatted.questions[0].answerOptions = [
        {
          ...formatted.questions[0].answerOptions[0],
          label: t('Decedent had dependants'),
        },
        {
          ...formatted.questions[0].answerOptions[1],
          label: t('Decedent did not have dependants'),
        },
      ];
      formatted.questions[0].children[0].answerOptions.properties =
        formatted.questions[0].children[0].answerOptions.properties.map(
          (item) =>
            DISABLED_BY_CHECKBOX.includes(item.key)
              ? {
                  ...item,
                  disabled: (name, { watch }) => {
                    const [slug, id] = name.split('.');
                    const value = watch(`${slug}.${id}.value.addressIsSame`);
                    return value;
                  },
                }
              : item
        );
    }

    return formatted;
  }, [questionListQuery.data, t]);
  const formKey = String(questionListQuery.dataUpdatedAt || '1');

  const handleSubmit = useCallback(
    async (form) => {
      await onSubmit(form);
      await questionListQuery.refetch();
    },
    [onSubmit, questionListQuery]
  );

  const onFormChange = useCallback(
    (value, { name, type }, { setValue, getValues }) => {
      if (type === 'change' && name.includes('addressIsSame')) {
        const [slug, id] = name.split('.');
        if (!!getValues(name)) {
          DISABLED_BY_CHECKBOX.forEach((field) => {
            setValue(
              `${slug}.${id}.value.${field}`,
              decedent[
                `residence${field.charAt(0).toUpperCase() + field.slice(1)}`
              ]
            );
          });
        } else {
          DISABLED_BY_CHECKBOX.forEach((field) => {
            setValue(`${slug}.${id}.value.${field}`, '');
          });
        }
      }
    },
    [decedent]
  );

  return payload.questions ? (
    <FormConstructor
      {...props}
      onSubmit={handleSubmit}
      questions={payload.questions}
      answers={payload.answers}
      onFormChange={onFormChange}
      key={formKey}
    />
  ) : (
    <Loader />
  );
};

export default DecedentDependants;
